/**
 * useZoomPan Hook
 * Feature: 012-svg-zoom-pan
 * Package: @alchemiesolns/svg-interactive (copied from svg-container)
 *
 * Provides interactive zoom and pan controls for SVG molecular visualizations.
 * Manages viewBox state, handles mouse/touch gestures, and provides imperative controls.
 */

import { useReducer, useRef, useCallback, useEffect, useState } from 'react';
import { useSVGRef } from '../contexts/SVGRefContext';
import type {
  UseZoomPanParams,
  UseZoomPanReturn,
  ViewBoxState,
  ViewBoxAction,
  DragState,
  BackgroundHandlers,
  SVGHandlers,
  ZoomPanControls,
  ZoomPanState,
} from '../types/useZoomPan.types';
import {
  isValidViewBoxState,
  parseViewBoxSafe,
  viewBoxToString,
} from '../utils/viewBoxCalculations';

/**
 * ViewBox state reducer
 * Handles all viewBox transformations (pan, zoom, reset)
 */
/** Apply zoom scale around a normalized center (same rules as ZOOM action). */
export function applyZoomToViewBox(
  base: ViewBoxState,
  scale: number,
  centerX: number,
  centerY: number,
  initialViewBox: ViewBoxState,
  minZoom?: number | "fit",
  maxZoom?: number
): ViewBoxState {
  return viewBoxReducer(
    base,
    { type: 'ZOOM', scale, centerX, centerY },
    initialViewBox,
    minZoom,
    maxZoom
  );
}

function viewBoxReducer(
  state: ViewBoxState,
  action: ViewBoxAction,
  initialViewBox?: ViewBoxState,
  minZoom?: number | "fit",
  maxZoom?: number
): ViewBoxState {
  switch (action.type) {
    case 'PAN': {
      const { deltaX, deltaY } = action;
      const next = {
        ...state,
        x: state.x - deltaX,
        y: state.y - deltaY,
      };
      return isValidViewBoxState(next) ? next : state;
    }

    case 'ZOOM': {
      const { scale, centerX, centerY } = action;

      // Calculate new dimensions
      let newWidth = state.width / scale;
      let newHeight = state.height / scale;

      // Apply zoom constraints if initial viewBox is provided
      if (initialViewBox) {
        // Calculate new scale factor (relative to initial)
        let newScale = initialViewBox.width / newWidth;

        // Apply minZoom constraint
        if (minZoom !== undefined) {
          const effectiveMinScale = minZoom === "fit" ? 1 : minZoom;
          if (newScale < effectiveMinScale) {
            newScale = effectiveMinScale;
            newWidth = initialViewBox.width / newScale;
            newHeight = initialViewBox.height / newScale;
          }
        }

        // Apply maxZoom constraint
        if (maxZoom !== undefined) {
          if (newScale > maxZoom) {
            newScale = maxZoom;
            newWidth = initialViewBox.width / newScale;
            newHeight = initialViewBox.height / newScale;
          }
        }
      }

      // Calculate offset to keep center point fixed
      const deltaWidth = state.width - newWidth;
      const deltaHeight = state.height - newHeight;

      // Adjust position to zoom toward center point
      const newX = state.x + deltaWidth * centerX;
      const newY = state.y + deltaHeight * centerY;

      const next = {
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      };
      return isValidViewBoxState(next) ? next : state;
    }

    case 'RESET': {
      return isValidViewBoxState(action.viewBox) ? action.viewBox : state;
    }

    default:
      return state;
  }
}

/**
 * useZoomPan Hook
 *
 * Provides complete zoom/pan functionality for SVG elements.
 * Handles mouse drag for panning and wheel events for zooming.
 *
 * When enabled, acts as a locked viewBox with zoom/pan transforms applied.
 * When disabled, passes through calculatedViewBox unchanged.
 *
 * @param params - Hook configuration parameters
 * @returns Handlers, state, and controls for zoom/pan
 *
 * @example
 * ```tsx
 * function MoleculeViewer({ config }: { config: string }) {
 *   const { viewBox, backgroundRect } = useSVGGeneration(config, 800, 600);
 *   const {
 *     backgroundHandlers,
 *     svgHandlers,
 *     viewBoxString,
 *     isPanning
 *   } = useZoomPan({
 *     calculatedViewBox: viewBox,
 *     enabled: true
 *   });
 *
 *   return (
 *     <svg viewBox={viewBoxString} {...svgHandlers}>
 *       <rect {...backgroundRect} {...backgroundHandlers} />
 *       {content}
 *     </svg>
 *   );
 * }
 * ```
 */
export function useZoomPan(params: UseZoomPanParams): UseZoomPanReturn {
  const {
    calculatedViewBox,
    svgRef: externalSvgRef,
    config = {},
    enabled = true,
    enableMouseWheelZoom = false,
    minZoom,
    maxZoom,
    initialZoomFactor: initialZoomFactorParam,
    initialViewport: initialViewportParam,
    onPanStart,
    onPan,
    onPanEnd,
    onZoom,
    onReset,
  } = params;

  const {
    panEnabled = true,
    zoomEnabled = true,
  } = config;

  // `calculatedViewBox` must always be the layout "fit" box. Do not pass a saved zoomed viewport here.
  const calculatedViewBoxState = parseViewBoxSafe(calculatedViewBox);

  const initialZoomFactor =
    initialZoomFactorParam !== undefined &&
    Number.isFinite(initialZoomFactorParam) &&
    initialZoomFactorParam > 0
      ? initialZoomFactorParam
      : 1;

  const calculatedViewBoxKey = viewBoxToString(calculatedViewBoxState);

  // Base viewBox reference (updated by reset to implement "Fit All") — always the layout fit box
  const baseViewBoxRef = useRef<ViewBoxState>(calculatedViewBoxState);

  // Initialize base viewBox on first render
  const isFirstRender = useRef(true);
  if (isFirstRender.current) {
    baseViewBoxRef.current = calculatedViewBoxState;
    isFirstRender.current = false;
  }

  const initialViewport =
    initialViewportParam !== undefined &&
    initialViewportParam !== null &&
    initialViewportParam !== ''
      ? initialViewportParam
      : undefined;

  // ViewBox state: hydrate from saved viewport if valid; else fit + initialZoomFactor
  const [viewBoxState, dispatch] = useReducer(
    (state: ViewBoxState, action: ViewBoxAction) =>
      viewBoxReducer(state, action, baseViewBoxRef.current, minZoom, maxZoom),
    {
      fit: calculatedViewBoxState,
      initialViewport,
      initialZoomFactor,
      minZoom,
      maxZoom,
    },
    ({ fit, initialViewport: iv, initialZoomFactor: iz, minZoom: mn, maxZoom: mx }) => {
      if (iv !== undefined && iv !== null && iv !== '') {
        const fromSaved = parseViewBoxSafe(iv as string | ViewBoxState);
        if (isValidViewBoxState(fromSaved)) {
          return fromSaved;
        }
      }
      return applyZoomToViewBox(fit, iz, 0.5, 0.5, fit, mn, mx);
    }
  );

  // Keep latest viewBox for pan/wheel handlers without putting `viewBoxState` in their
  // dependency arrays — otherwise every PAN updates state, recreates handlers, and the
  // window `mousemove` listener is removed/re-added mid-drag (broken pan).
  const viewBoxStateRef = useRef(viewBoxState);
  viewBoxStateRef.current = viewBoxState;

  const prevCalculatedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevCalculatedKeyRef.current === calculatedViewBoxKey) return;
    prevCalculatedKeyRef.current = calculatedViewBoxKey;
    // Keep the "Fit All" / zoom-percentage reference in sync with the current
    // content's fit box, but do NOT snap the live viewport when the content
    // changes — the user's zoom/pan is preserved across content changes (e.g.
    // every arrow push in the mechanisms player). A consumer that genuinely
    // wants a re-fit on a content change calls reset() itself (e.g.
    // reaction-scheme export parity).
    //
    // This effect used to also `dispatch({ type: 'RESET' })` on every change — a
    // reaction-scheme export-parity device (introduced with the build/export
    // scaling unification) that every zoom-pan consumer inherited, snapping the
    // player to Fit-All after each push. Removed; explicit reset() is the seam.
    baseViewBoxRef.current = parseViewBoxSafe(calculatedViewBoxKey);
  }, [calculatedViewBoxKey]);

  // Track panning state to ensure cursor updates properly
  const [isPanning, setIsPanning] = useState(false);

  // SVG ref resolution: explicit param > SVGRefProvider context > internal fallback
  const contextRef = useSVGRef({ optional: true });
  const internalSvgRef = useRef<SVGSVGElement>(null);
  const svgRef = externalSvgRef || contextRef?.svgRef || internalSvgRef;

  // Drag state for panning
  const dragStateRef = useRef<DragState | null>(null);

  // Pointer cache for touch gestures (pinch-to-zoom)
  const pointerCacheRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const previousDistanceRef = useRef<number | null>(null);

  /**
   * Handle mouse down on background - start pan
   */
  const handleBackgroundMouseDown = useCallback((event: React.MouseEvent<SVGRectElement>) => {
    if (!enabled || !panEnabled) return;

    // Only pan on left mouse button
    if (event.button !== 0) return;

    event.preventDefault();

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      isDragging: true,
    };
    setIsPanning(true);

    // Fire onPanStart callback
    onPanStart?.();
  }, [enabled, panEnabled, onPanStart]);

  /**
   * Handle mouse move - perform pan
   */
  const handleMouseMove = useCallback((event: MouseEvent) => {
    const dragState = dragStateRef.current;
    if (!dragState || !dragState.isDragging) return;

    const svgElement = svgRef.current;
    if (!svgElement) return;

    const vb = viewBoxStateRef.current;

    // Calculate screen delta
    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;

    // Convert screen delta to SVG delta
    const scaleX = vb.width / svgElement.clientWidth;
    const scaleY = vb.height / svgElement.clientHeight;

    const svgDeltaX = deltaX * scaleX;
    const svgDeltaY = deltaY * scaleY;

    // Update viewBox
    dispatch({
      type: 'PAN',
      deltaX: svgDeltaX,
      deltaY: svgDeltaY,
    });

    // Update drag start position
    dragStateRef.current = {
      ...dragState,
      startX: event.clientX,
      startY: event.clientY,
    };

    // Note: `onPan` receives pre-PAN `vb`; callers rarely use this during drag.
    onPan?.(vb);
  }, [svgRef, onPan]);

  /**
   * Handle mouse up - end pan
   */
  const handleMouseUp = useCallback(() => {
    const wasDragging = dragStateRef.current?.isDragging;
    dragStateRef.current = null;
    setIsPanning(false);

    // Fire onPanEnd callback if we were panning
    if (wasDragging) {
      onPanEnd?.();
    }
  }, [onPanEnd]);

  /**
   * Handle wheel event - perform zoom
   */
  const handleWheel = useCallback((event: React.WheelEvent<SVGSVGElement>) => {
    if (!enabled || !zoomEnabled) return;

    // Check enableMouseWheelZoom mode
    if (enableMouseWheelZoom === false) {
      // Don't zoom, let event bubble for scrolling
      return;
    }

    if (enableMouseWheelZoom === 'modifier') {
      // Require Ctrl or Cmd key
      if (!event.ctrlKey && !event.metaKey) {
        // Don't preventDefault - let scroll bubble
        return;
      }
    }

    // enableMouseWheelZoom is 'always' or modifier key is pressed
    event.preventDefault();

    const svgElement = event.currentTarget;
    if (!svgElement) return;

    // Calculate zoom scale from wheel delta
    // Negative deltaY = scroll up = zoom in (scale > 1)
    // Positive deltaY = scroll down = zoom out (scale < 1)
    const delta = -event.deltaY;
    const zoomFactor = delta > 0 ? 1.1 : 0.9;

    // Get mouse position in screen coordinates
    const rect = svgElement.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    // Convert to normalized coordinates (0-1 range)
    const centerX = mouseX / rect.width;
    const centerY = mouseY / rect.height;

    // Apply zoom
    dispatch({
      type: 'ZOOM',
      scale: zoomFactor,
      centerX,
      centerY,
    });

    // Fire onZoom callback with zoom factor
    const vb = viewBoxStateRef.current;
    const newViewBoxWidth = vb.width / zoomFactor;
    const absoluteZoomFactor = baseViewBoxRef.current.width / newViewBoxWidth;
    onZoom?.(absoluteZoomFactor);
  }, [enabled, zoomEnabled, enableMouseWheelZoom, onZoom]);

  /**
   * Handle pointer down - start touch gesture
   */
  const handlePointerDown = useCallback((event: React.PointerEvent<SVGRectElement>) => {
    if (!enabled) return;

    event.preventDefault();

    // Add pointer to cache
    pointerCacheRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    // If only one pointer, start pan
    if (pointerCacheRef.current.size === 1) {
      dragStateRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        isDragging: true,
      };
      setIsPanning(true);

      // Capture the pointer to ensure we receive events even outside the element
      const target = event.currentTarget;
      try {
        target.setPointerCapture(event.pointerId);
      } catch (e) {
        // Pointer capture may fail in some scenarios, fallback to global listeners
        console.warn('[useZoomPan] Pointer capture failed:', e);
      }

      // Fire onPanStart callback
      onPanStart?.();
    }
  }, [enabled, onPanStart]);

  /**
   * Handle pointer move - perform touch pan or pinch-zoom
   */
  const handlePointerMove = useCallback((event: React.PointerEvent<SVGRectElement>) => {
    if (!enabled) return;

    const pointerCache = pointerCacheRef.current;
    if (!pointerCache.has(event.pointerId)) return;

    event.preventDefault();

    // Update pointer position in cache
    pointerCache.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    const pointers = Array.from(pointerCache.values());

    if (pointers.length === 2 && zoomEnabled) {
      // Pinch-to-zoom with two pointers
      const [p1, p2] = pointers;

      // Calculate current distance between pointers
      const currentDistance = Math.hypot(p2.x - p1.x, p2.y - p1.y);

      if (previousDistanceRef.current !== null) {
        // Calculate scale factor from distance change
        const scale = currentDistance / previousDistanceRef.current;

        // Get SVG element for coordinate calculations
        const svgElement = svgRef.current;
        if (!svgElement) return;

        // Calculate midpoint between pointers (zoom center)
        const rect = svgElement.getBoundingClientRect();
        const midX = (p1.x + p2.x) / 2 - rect.left;
        const midY = (p1.y + p2.y) / 2 - rect.top;

        // Convert to normalized coordinates (0-1)
        const centerX = midX / rect.width;
        const centerY = midY / rect.height;

        // Apply zoom
        dispatch({
          type: 'ZOOM',
          scale,
          centerX,
          centerY,
        });

        const vbPinch = viewBoxStateRef.current;
        const newViewBoxWidth = vbPinch.width / scale;
        const absoluteZoomFactor = baseViewBoxRef.current.width / newViewBoxWidth;
        onZoom?.(absoluteZoomFactor);
      }

      previousDistanceRef.current = currentDistance;
    } else if (pointers.length === 1 && panEnabled) {
      // Single-pointer pan
      const dragState = dragStateRef.current;
      if (!dragState || !dragState.isDragging) return;

      const svgElement = svgRef.current;
      if (!svgElement) return;

      const vb = viewBoxStateRef.current;

      // Calculate screen delta
      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      // Convert screen delta to SVG delta
      const scaleX = vb.width / svgElement.clientWidth;
      const scaleY = vb.height / svgElement.clientHeight;

      const svgDeltaX = deltaX * scaleX;
      const svgDeltaY = deltaY * scaleY;

      // Update viewBox
      dispatch({
        type: 'PAN',
        deltaX: svgDeltaX,
        deltaY: svgDeltaY,
      });

      // Update drag start position
      dragStateRef.current = {
        ...dragState,
        startX: event.clientX,
        startY: event.clientY,
      };

      onPan?.(vb);
    }
  }, [enabled, panEnabled, zoomEnabled, svgRef, onPan, onZoom]);

  /**
   * Handle pointer up - end touch gesture
   */
  const handlePointerUp = useCallback((event: React.PointerEvent<SVGRectElement>) => {
    event.preventDefault();

    // Release pointer capture
    const target = event.currentTarget;
    if (target.hasPointerCapture(event.pointerId)) {
      try {
        target.releasePointerCapture(event.pointerId);
      } catch (e) {
        // Ignore release errors
      }
    }

    // Remove pointer from cache
    pointerCacheRef.current.delete(event.pointerId);

    // Reset previous distance if no pointers left
    if (pointerCacheRef.current.size < 2) {
      previousDistanceRef.current = null;
    }

    // End pan if no pointers left
    if (pointerCacheRef.current.size === 0) {
      const wasDragging = dragStateRef.current?.isDragging;
      dragStateRef.current = null;
      setIsPanning(false);

      // Fire onPanEnd callback if we were panning
      if (wasDragging) {
        onPanEnd?.();
      }
    }
  }, [onPanEnd]);

  /**
   * Handle pointer cancel - clean up cancelled pointer
   */
  const handlePointerCancel = useCallback((event: React.PointerEvent<SVGRectElement>) => {
    event.preventDefault();

    // Release pointer capture
    const target = event.currentTarget;
    if (target.hasPointerCapture(event.pointerId)) {
      try {
        target.releasePointerCapture(event.pointerId);
      } catch (e) {
        // Ignore release errors
      }
    }

    // Remove pointer from cache
    pointerCacheRef.current.delete(event.pointerId);

    // Reset previous distance if no pointers left
    if (pointerCacheRef.current.size < 2) {
      previousDistanceRef.current = null;
    }

    // End pan if no pointers left
    if (pointerCacheRef.current.size === 0) {
      const wasDragging = dragStateRef.current?.isDragging;
      dragStateRef.current = null;
      setIsPanning(false);

      // Fire onPanEnd callback if we were panning
      if (wasDragging) {
        onPanEnd?.();
      }
    }
  }, [onPanEnd]);

  /**
   * Set up global mouse event listeners
   */
  useEffect(() => {
    if (!enabled || !panEnabled) return;

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [enabled, panEnabled, handleMouseMove, handleMouseUp]);

  /**
   * Set up safety handlers to prevent stuck drag state
   * Handles: window blur, visibility change, and Escape key
   */
  useEffect(() => {
    const cancelDrag = () => {
      if (dragStateRef.current?.isDragging) {
        const wasDragging = true;
        dragStateRef.current = null;
        setIsPanning(false);
        pointerCacheRef.current.clear();
        previousDistanceRef.current = null;

        if (wasDragging) {
          onPanEnd?.();
        }
      }
    };

    const handleWindowBlur = () => {
      // Cancel drag when window loses focus (Alt+Tab, etc.)
      cancelDrag();
    };

    const handleVisibilityChange = () => {
      // Cancel drag when tab becomes hidden
      if (document.hidden) {
        cancelDrag();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Allow user to cancel drag with Escape key
      if (event.key === 'Escape') {
        cancelDrag();
      }
    };

    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onPanEnd]);

  /**
   * Keyboard navigation for accessibility
   * Arrow keys for pan, +/- for zoom
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle if SVG element is focused or user is interacting with it
      const svgElement = svgRef.current;
      if (!enabled || !svgElement) return;

      // Check if the active element is within our SVG or is the SVG itself
      const activeElement = document.activeElement;
      if (activeElement !== svgElement && !svgElement.contains(activeElement)) {
        return; // Don't handle keyboard events when SVG is not focused
      }

      // Pan with arrow keys
      if (panEnabled && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        // Don't intercept arrow keys when focus is on NavigableTree (uses aria-activedescendant pattern)
        const role = activeElement?.getAttribute('role');
        if (role === 'tree' || role === 'treeitem') {
          return;
        }

        event.preventDefault();
        const panStep = viewBoxStateRef.current.width * 0.1; // Pan by 10% of current viewBox width

        switch (event.key) {
          case 'ArrowUp':
            dispatch({ type: 'PAN', deltaX: 0, deltaY: panStep });
            break;
          case 'ArrowDown':
            dispatch({ type: 'PAN', deltaX: 0, deltaY: -panStep });
            break;
          case 'ArrowLeft':
            dispatch({ type: 'PAN', deltaX: panStep, deltaY: 0 });
            break;
          case 'ArrowRight':
            dispatch({ type: 'PAN', deltaX: -panStep, deltaY: 0 });
            break;
        }
        return;
      }

      // Zoom with +/- keys
      if (zoomEnabled && (event.key === '+' || event.key === '=' || event.key === '-' || event.key === '_')) {
        event.preventDefault();
        const zoomFactor = (event.key === '+' || event.key === '=') ? 1.2 : 0.8;
        dispatch({
          type: 'ZOOM',
          scale: zoomFactor,
          centerX: 0.5,
          centerY: 0.5,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, panEnabled, zoomEnabled, svgRef]);

  /**
   * Reset to fit viewBox with `initialZoomFactor` applied (same as first paint after load).
   */
  const reset = useCallback(() => {
    baseViewBoxRef.current = calculatedViewBoxState;
    const next = applyZoomToViewBox(
      calculatedViewBoxState,
      initialZoomFactor,
      0.5,
      0.5,
      calculatedViewBoxState,
      minZoom,
      maxZoom
    );
    dispatch({
      type: 'RESET',
      viewBox: next,
    });
    onReset?.();
  }, [calculatedViewBoxState, initialZoomFactor, minZoom, maxZoom, onReset]);

  /**
   * Imperative pan control
   */
  const pan = useCallback((deltaX: number, deltaY: number) => {
    dispatch({
      type: 'PAN',
      deltaX,
      deltaY,
    });
  }, []);

  /**
   * Imperative zoom control (Phase 4)
   */
  const zoom = useCallback((scale: number, centerX = 0.5, centerY = 0.5) => {
    // Programmatic zoom should remain available even when user gesture zoom is disabled
    // (e.g. sidebar-controlled zoom in reaction preview).
    if (!enabled) return;

    dispatch({
      type: 'ZOOM',
      scale,
      centerX,
      centerY,
    });
  }, [enabled]);

  // Background handlers (spread onto background rect)
  const backgroundHandlers: BackgroundHandlers = {
    onMouseDown: handleBackgroundMouseDown,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
  };

  // SVG handlers (spread onto SVG element)
  // Note: tabIndex is NOT included - NavigableTree's <g role="tree"> handles focus
  const svgHandlers: SVGHandlers = {
    onWheel: zoomEnabled ? handleWheel : undefined,
  };

  // Controls for imperative access
  const controls: ZoomPanControls = {
    reset,
    pan,
    zoom,
  };

  // Current state
  const currentZoomFactor =
    viewBoxState.width > 0
      ? baseViewBoxRef.current.width / viewBoxState.width
      : 1;

  const state: ZoomPanState = {
    zoomFactor: enabled ? currentZoomFactor : 1.0,
    isPanning: enabled ? isPanning : false,
  };

  // Convert viewBox state to string
  // When disabled, pass through calculatedViewBox unchanged
  const viewBox = enabled
    ? viewBoxToString(viewBoxState)
    : viewBoxToString(calculatedViewBoxState);

  return {
    // Handlers (empty when disabled)
    backgroundHandlers: enabled ? backgroundHandlers : {},
    svgHandlers: enabled ? svgHandlers : {},
    svgRef,

    // State
    viewBox,
    state,

    // Controls
    controls,
  };
}
